import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import styled from 'styled-components';

import useTranslation from '../../customHooks/translate';
import LanguageDropdown from '../common/language-dropdown';
import { landingHeaderlinks } from 'consts';

import { fadeInDown } from './landing-motion';

const Header = () => {
  const translation = useTranslation();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleSectionClick =
    (sectionId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      setIsMenuOpen(false);

      if (location.pathname !== '/') {
        return;
      }

      event.preventDefault();
      const targetHash = `#${sectionId}`;
      const element = document.getElementById(sectionId);

      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', targetHash);
    };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  const renderNavLink = (link: {
    name: string;
    url: string;
    external?: boolean;
    page?: boolean;
  }) => {
    if (link.external) {
      return (
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[16px] font-normal"
          onClick={closeMobileMenu}
        >
          {link.name}
        </a>
      );
    }

    if (link.page) {
      return (
        <Link to={link.url} onClick={closeMobileMenu}>
          <span className="text-[16px] font-normal">{link.name}</span>
        </Link>
      );
    }

    return (
      <Link to={`/#${link.url}`} onClick={handleSectionClick(link.url)}>
        <span className="text-[16px] font-normal">{link.name}</span>
      </Link>
    );
  };

  return (
    <header className="lg:cc relative z-40 mt-[20px] flex items-center justify-between px-[20px] lg:mt-[40px] lg:px-[unset]">
      <div
        className={`flex items-center ${fadeInDown}`}
        style={{ animationDelay: '0s' }}
      >
        <figure className="h-[36px] w-[108px] md:mr-10">
          <Link to="/">
            <img src="assets/logo.png" alt="logo" />
          </Link>
        </figure>
      </div>
      <ul
        className={`hidden items-center gap-[30px] lg:flex ${fadeInDown}`}
        style={{ animationDelay: '0.08s' }}
      >
        {landingHeaderlinks(translation).map((link) => (
          <li key={link.name}>{renderNavLink(link)}</li>
        ))}
      </ul>
      <div
        className={`block flex items-center gap-[8px] lg:hidden ${fadeInDown}`}
        style={{ animationDelay: '0.08s' }}
      >
        <LanguageDropdown />
        <BurgerIcon onClick={toggleMenu} isOpen={isMenuOpen}>
          <span />
          <span />
          <span />
        </BurgerIcon>
      </div>

      <div
        className={`hidden items-center gap-[8px] lg:flex ${fadeInDown}`}
        style={{ animationDelay: '0.12s' }}
      >
        <Link to="/login">
          <button className="flex h-[38px] items-center gap-[6px] rounded-[21px] bg-[rgba(247,249,252,1)] px-[24px] py-[8px] text-[16px] font-medium text-[rgba(70,106,251,1)] transition-all hover:bg-gray-100">
            <img
              className="h-[22px] w-[22px]"
              src="assets/profile.svg"
              alt=""
            />
            {translation.home.Header.LogIn}
          </button>
        </Link>
        <LanguageDropdown />
      </div>

      <MobileMenu
        key={isMenuOpen ? 'menu-open' : 'menu-close'}
        isOpen={isMenuOpen}
        className="border-b border-[rgba(41,45,50,0.1)] lg:border-b-0"
      >
        <ul>
          {landingHeaderlinks(translation).map((link) => (
            <li key={link.name} className="font-normal">
              {renderNavLink(link)}
            </li>
          ))}
        </ul>
        <div className="mb-[16px] w-full px-5">
          <Link to="/login">
            <button className="flex w-full items-center justify-center gap-[8px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white">
              {translation.login.SignInButton}
            </button>
          </Link>
        </div>
      </MobileMenu>
    </header>
  );
};

const BurgerIcon = styled.div<{ isOpen: boolean }>`
  width: 25px;
  height: 17px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;

  span {
    width: 100%;
    height: 2px;
    background: #000;
    transition: all 0.3s ease;
    border-radius: 10px;
  }

  ${({ isOpen }) =>
    isOpen &&
    `
    span:nth-child(1) {
      transform: rotate(45deg) translate(5px, 5px);
    }
    span:nth-child(2) {
      opacity: 0;
    }
    span:nth-child(3) {
      transform: rotate(-45deg) translate(5px, -5px);
    }
  `}
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: ${({ isOpen }) => (isOpen ? '38px' : '-100%')};
  left: 0;
  width: 100%;
  background: white;
  transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-100%')});
  z-index: 9;

  ul {
    list-style: none;
    padding: 20px;
    margin: 0;

    li {
      padding: 12px 0;
      border-bottom: 1px solid #eee;
      color: #333;

      a {
        text-decoration: none !important;
        display: block;
      }
    }
  }
`;

export default Header;
